import Ionicons from 'react-native-vector-icons/Ionicons';
import { useNavigation } from '@react-navigation/native';
import { useEffect, useMemo, useState } from 'react';
import {
  Image,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { useTheme } from '../context/ThemeContext';
import { getThemeColors } from '../utils/themeStyles';
import { useAuth } from '../context/AuthContext';
import { listUsChats, UsChatSummary } from '../api/userStories';
import { listScriptedChats, ScriptedChatSummary } from '../api/scriptedChats';
import { useT } from '../i18n/useT';
import type { MessageKey } from '../i18n';
import { groupByPerson, personKey } from '../utils/chatPeople';
import { useUpdates } from '../context/UpdatesContext';

/** Preview length, once. It was written twice, with two values drifting apart card by card. */
const PREVIEW_CHARS = 90;

// Thread label from the reason the engine recorded.
// ⚠️ KEYS, not translated strings — a module-level map is built before any hook has run.
const REASON_LABEL: Record<string, MessageKey> = {
  blocked: 'chat.tone.blocked',
  refused: 'chat.tone.refused',
  validated: 'chat.tone.validated',
  handover: 'chat.tone.handover',   // the player has spoken, the character has not yet (CCT-345)
};

// ⚠️ `at`, not `t` — the local held the TIMESTAMP and would shadow the translate function.
const relTime = (iso: string | null, t: (k: MessageKey, v?: Record<string, unknown>) => string) => {
  if (!iso) return '';
  const at = new Date(iso).getTime();
  if (Number.isNaN(at)) return '';
  const min = Math.floor(Math.max(0, Date.now() - at) / 60000);
  if (min < 1) return t('notif.justNow');
  if (min < 60) return t('notif.ago.min', { n: min });
  const h = Math.floor(min / 60);
  if (h < 24) return t('notif.ago.hour', { n: h });
  return new Date(iso).toLocaleDateString();
};

export default function ChatScreen() {
  const t = useT();
  const navigation = useNavigation<any>();
  const { isDarkTheme } = useTheme();
  const colors = getThemeColors(isDarkTheme);
  const { width } = useWindowDimensions();
  const isWebDesktop = Platform.OS === 'web' && width >= 768;
  const { getAccessToken } = useAuth();
  // ⚠️ SEEING THE LIST IS NOT READING THE CONVERSATION — CCT-350. Marking the area seen when
  // this screen opened made the badge vanish before the player had even seen which conversation
  // it was about: it announced something, then removed the announcement on the way in. It is
  // OPENING a conversation that reads it (see `openConversation` below, and the conversation
  // screens themselves, which also cover the notification deep-links).
  const { markSeen } = useUpdates();

  // Fils du MOTEUR (hint / refus / validation) — affichés à côté des discussions scénarisées
  // pour que le joueur retrouve TOUT au même endroit (CCT-97).
  const [engineChats, setEngineChats] = useState<UsChatSummary[]>([]);
  const [scriptedChats, setScriptedChats] = useState<ScriptedChatSummary[]>([]);
  const [missionFilter, setMissionFilter] = useState<number | null>(null);
  useEffect(() => { void (async () => {
    try { setEngineChats((await listUsChats(getAccessToken())).chats ?? []); }
    catch { /* pas de fil moteur (ou hors ligne) : la liste scénarisée suffit */ }
  })(); }, [getAccessToken]);
  // ⚠️ DEUX APPELS SÉPARÉS, ET DEUX `catch` SÉPARÉS. Les fusionner ferait qu'une source en
  // panne viderait l'autre : le joueur verrait une liste vide au lieu de la moitié qui marche.
  useEffect(() => { void (async () => {
    try { setScriptedChats((await listScriptedChats(getAccessToken())).chats ?? []); }
    catch { /* runtime plus ancien ou hors ligne : on n'invente rien, la liste reste sans */ }
  })(); }, [getAccessToken]);

  const missionLabel = (id: number, title?: string | null) => title || t('chat.missionN', { id });

  // ONE ENTRY PER CONVERSATION, whichever source it comes from — CCT-347.
  //
  // Two sources tell the same story: the AUTHOR's conversations (`/me/stories/chats`) and the
  // threads the ENGINE opened around a task (`/me/user-stories/chats`). The player does not think
  // in sources, they think in PEOPLE — so both are normalised here, then grouped by person.
  //
  // ⚠️ THE TARGET TRAVELS WITH THE ROW. It used to be recomputed at click time from the kind of
  // card, and the scripted branch opened "the first chat of the mission" instead of the one that
  // was tapped. Carried by the entry, it cannot drift from what is displayed.
  const conversations = useMemo(() => {
    const clip = (s: string) => (s.length > PREVIEW_CHARS ? s.slice(0, PREVIEW_CHARS) + '…' : s);
    const rows = [
      ...scriptedChats.map(c => ({
        key: `script-${c.missionId}-${c.itemId}`,
        personKey: personKey(c.characterId, c.characterName),
        characterId: c.characterId ?? null,
        name: c.characterName, avatar: c.avatarUrl,
        missionId: c.missionId, missionTitle: c.missionTitle,
        detail: c.questionCount > 0
          ? t(c.questionCount > 1 ? 'chat.questionMany' : 'chat.questionOne', { count: c.questionCount })
          : c.title,
        preview: clip(c.lastMessage), at: null as string | null, open: false,
        target: { missionId: c.missionId, openItemId: c.itemId } as Record<string, any>,
      })),
      ...engineChats.map(c => ({
        key: `engine-${c.chatId}`,
        personKey: personKey(c.characterId, c.characterName),
        characterId: c.characterId ?? null,
        name: c.characterName,
        avatar: c.avatarkey && c.avatarkey.startsWith('http') ? c.avatarkey : null,
        missionId: c.missionId, missionTitle: c.missionTitle,
        detail: REASON_LABEL[c.reason] ? t(REASON_LABEL[c.reason]) : t('chat.tone.default'),
        preview: clip(c.lastMessage), at: c.updatedAt, open: !c.closed,
        // The task's thread, designated by ITS element — the same intent as the engine's
        // notification route (`usChatElementId`), so both paths open the same screen.
        target: { missionId: c.missionId, usChatElementId: c.elementId } as Record<string, any>,
      })),
    ];
    return missionFilter == null ? rows : rows.filter(r => r.missionId === missionFilter);
  }, [scriptedChats, engineChats, missionFilter, t]);

  // Grouped BY PERSON, most recently active first. A conversation with no timestamp (the authored
  // ones carry none) sorts last inside its group rather than jumping to the top.
  const people = useMemo(() => groupByPerson(conversations), [conversations]);

  // Which person's list is unfolded. A single one at a time: the point of grouping is a short
  // list, and everything unfolded is the flat list we came from.
  const [openPerson, setOpenPerson] = useState<string | null>(null);

  // Mission filter: the missions present in EITHER of the two sources.
  const missionOptions = useMemo(() => {
    const m = new Map<number, string>();
    scriptedChats.forEach(c => m.set(c.missionId, missionLabel(c.missionId, c.missionTitle)));
    engineChats.forEach(c => {
      if (!m.has(c.missionId)) m.set(c.missionId, missionLabel(c.missionId, c.missionTitle));
    });
    return [...m.entries()].map(([missionId, title]) => ({ missionId, title }));
  }, [scriptedChats, engineChats, t]);

  // A conversation ALWAYS opens inside the CHAPTER screen: it is the one carrying the mission
  // layout (progression / team / rewards sidebar) — CCT-97.
  //
  // ⚠️ THE CONVERSATION THAT WAS CLICKED, NOT "THE FIRST ONE" (CCT-347). This screen used to open
  // scripted chats with `openFirstChat: true`, an intent meant for a notification that designates
  // NO element ("a character wants to talk to you"). Here the element is known — it is the row
  // that was tapped — and the first chat of the mission was served instead: with six
  // conversations listed, five of them opened the wrong one.
  const openConversation = (target: Record<string, any>) => {
    markSeen('chat');            // opening it IS reading it (CCT-350)
    navigation.navigate('ChapterDetail', target);
  };

  // SELECTING A PERSON OPENS THEIR TIMELINE — CCT-355.
  //
  // ⚠️ THE FILTER THE PLAYER ASKED FOR LIVES THERE, AND NOWHERE ELSE. Tapping a person only
  // unfolded their conversations, and each of those opened a single thread — a screen with one
  // task in it, where filtering by task means nothing. The timeline is the screen that merges
  // everything this character ever said, which is precisely what needs filtering. The player
  // asked twice, looked twice, and found nothing.
  //
  // The unfolded list stays reachable through the chevron: it is the way to jump to ONE precise
  // conversation (CCT-347), which the timeline does not replace.
  const openPersonTimeline = (g: { chats: typeof conversations }) => {
    const head = g.chats[0];
    // No runtime identity (a name spoken outside the cast): there is no timeline to open, so we
    // fall back on what does exist — the conversation itself.
    if (head.characterId == null) { openConversation(head.target); return; }
    markSeen('chat');
    navigation.navigate('ChapterDetail',
      { missionId: head.missionId, characterChatId: head.characterId });
  };


  /** Avatar, or the coloured initial — which SAYS a photo is missing instead of inventing one. */
  const Avatar = ({ url, name, badge }: { url: string | null; name: string | null; badge?: number }) => (
    <View style={styles.avatarWrapper}>
      {url ? (
        <View style={styles.avatarImgWrap}>
          <Image source={{ uri: url }} style={styles.avatarImg} resizeMode="cover" />
        </View>
      ) : (
        <View style={[styles.avatar, { backgroundColor: colors.primary }]}>
          <Text style={[styles.avatarText, { color: colors.badgeText }]}>
            {(name || '?').slice(0, 1).toUpperCase()}
          </Text>
        </View>
      )}
      {!!badge && (
        <View style={[styles.unreadBubble, { backgroundColor: colors.badgeBackground }]}>
          <Text style={[styles.unreadText, { color: colors.badgeText }]}>{badge}</Text>
        </View>
      )}
    </View>
  );

  /** ONE card per PERSON. Tapping it unfolds THEIR conversations — each opening its own. */
  const renderPerson = (g: { key: string; chats: typeof conversations }) => {
    const head = g.chats[0];
    const unfolded = openPerson === g.key;
    const open = g.chats.filter(c => c.open).length;
    const missions = new Set(g.chats.map(c => c.missionId));
    return (
      <View key={g.key} style={[styles.card, isWebDesktop && styles.cardDesktop,
                                { backgroundColor: colors.cardBackground }]}>
        {/* ⚠️ THE CHEVRON IS OUTSIDE THE CARD'S TOUCHABLE, not nested inside it. On the web a
            click on a nested touchable ALSO reaches the one wrapping it: the list would unfold
            and the conversation open at the same time. Two zones side by side, one intent each. */}
        <View style={styles.cardRow}>
          <TouchableOpacity activeOpacity={0.75} style={styles.cardMain}
            onPress={() => openPersonTimeline(g)}>
            <Avatar url={head.avatar} name={head.name} badge={open || undefined} />
            <View style={{ flex: 1, minWidth: 0 }}>
              <View style={styles.cardTopRow}>
                <Text numberOfLines={1} style={[styles.missionTitle, { color: colors.textPrimary }]}>
                  {head.name || t('chat.character')}
                </Text>
                <Text style={[styles.timestamp, { color: colors.textTertiary }]}>
                  {relTime(head.at, t)}
                </Text>
              </View>
              <Text numberOfLines={1} style={[styles.tutorName, { color: colors.textSecondary }]}>
                {missions.size > 1
                  ? t('chat.conversationMany', { count: g.chats.length })
                  : missionLabel(head.missionId, head.missionTitle)}
                {missions.size > 1 ? '' : ` · ${t(g.chats.length > 1 ? 'chat.conversationMany' : 'chat.conversationOne', { count: g.chats.length })}`}
                {open > 0 ? ` · ${t('chat.openCount', { count: open })}` : ''}
              </Text>
              {!unfolded && (
                <Text numberOfLines={2} style={[styles.lastMessage, { color: colors.textSecondary }]}>
                  {head.preview}
                </Text>
              )}
            </View>
          </TouchableOpacity>
          {/* The chevron, and it alone, unfolds: the card itself opens the conversation. */}
          <TouchableOpacity hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            onPress={() => setOpenPerson(cur => (cur === g.key ? null : g.key))}>
            <Ionicons name={unfolded ? 'chevron-up' : 'chevron-down'} size={18}
              color={colors.textTertiary} />
          </TouchableOpacity>
        </View>

        {/* The person's conversations. ⚠️ EACH ROW CARRIES ITS OWN TARGET (see `conversations`):
            that is exactly what was broken — every authored row opened the FIRST chat of the
            mission, so five rows out of six led to the wrong conversation. */}
        {unfolded && g.chats.map(c => (
          <TouchableOpacity key={c.key} activeOpacity={0.75}
            onPress={() => openConversation(c.target)}
            style={[styles.threadRow, { borderTopColor: colors.border }]}>
            <View style={{ flex: 1, minWidth: 0 }}>
              <Text numberOfLines={1} style={[styles.tutorName, { color: colors.textPrimary }]}>
                {c.detail}
                {missions.size > 1 ? ` · ${missionLabel(c.missionId, c.missionTitle)}` : ''}
              </Text>
              <Text numberOfLines={2} style={[styles.lastMessage, { color: colors.textSecondary }]}>
                {c.preview}
              </Text>
            </View>
            <Ionicons name="chevron-forward" size={16} color={colors.textTertiary} />
          </TouchableOpacity>
        ))}
      </View>
    );
  };

  const totalConv = conversations.length;

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: colors.background }]}>
      {/* Header */}
      <View
        style={[styles.header, { backgroundColor: colors.surface, borderBottomColor: colors.border }]}
      >
        <View style={styles.headerRow}>
          <View>
            <Text style={[styles.headerTitle, { color: colors.textPrimary }]}>{t('chat.messages')}</Text>
            <Text style={[styles.headerSubtitle, { color: colors.textSecondary }]}>
              {t(totalConv > 1 ? 'chat.conversationMany' : 'chat.conversationOne', { count: totalConv })}
            </Text>
          </View>
          {/* Plus de compteur de NON-LUS : il venait de `unreadCount`, une valeur CODÉE EN
              DUR dans les conversations bundlées. Les fils du moteur (`UsChatSummary`) n'en
              portent aucun — afficher un nombre inventé vaut moins que ne rien afficher. */}
        </View>

        {/* Filtre par mission — le centre de messagerie traverse toutes les missions. */}
        {missionOptions.length > 1 && (
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={styles.filterRow}
          >
            {[{ missionId: null as number | null, title: t('chat.all') }, ...missionOptions].map(o => {
              const on = missionFilter === o.missionId;
              return (
                <TouchableOpacity
                  key={o.missionId ?? 'all'}
                  onPress={() => setMissionFilter(o.missionId)}
                  activeOpacity={0.75}
                  style={[styles.chip, {
                    backgroundColor: on ? colors.primary : colors.surfaceSecondary,
                    borderColor: on ? colors.primary : colors.border,
                  }]}
                >
                  <Text numberOfLines={1} style={[styles.chipText, { color: on ? colors.badgeText : colors.textSecondary }]}>
                    {o.title}
                  </Text>
                </TouchableOpacity>
              );
            })}
          </ScrollView>
        )}
      </View>

      {/* Conversation list */}
      <ScrollView
        style={[styles.list, { backgroundColor: colors.background }]}
        contentContainerStyle={[
          styles.listContent,
          isWebDesktop && styles.listContentDesktop,
          isWebDesktop && { width: '100%' },
        ]}
        showsVerticalScrollIndicator={false}
      >
        {/* ONE card per PERSON (CCT-347): the player thinks in interlocutors, not in sources —
            the author's conversations and the engine's threads sit in the same card. */}
        {people.map(renderPerson)}
        {totalConv === 0 && (
          <View style={styles.empty}>
            <Ionicons name="chatbubbles-outline" size={40} color={colors.textTertiary} />
            <Text style={[styles.emptyText, { color: colors.textTertiary }]}>
              {missionFilter != null
                ? t('chat.none')
                : t('chat.noneUnlocked')}
            </Text>
          </View>
        )}
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },

  header: {
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerTitle: { fontSize: 22, fontWeight: '700' },
  headerSubtitle: { fontSize: 13, marginTop: 2 },
  totalUnreadBadge: { borderRadius: 12, paddingHorizontal: 10, paddingVertical: 4 },
  totalUnreadText: { fontSize: 12, fontWeight: '700' },

  list: { flex: 1 },
  listContent: { padding: 16 },
  listContentDesktop: {
    maxWidth: 960,
    alignSelf: 'center',
  },

  // CCT-347 — a person's conversation, inside their card. A separator rather than a box: they
  // belong to the card above, they are not cards of their own.
  threadRow: { flexDirection: 'row', alignItems: 'center', gap: 10,
               paddingTop: 10, marginTop: 10, borderTopWidth: 1 },
  card: {
    borderRadius: 14,
    padding: 16,
    marginBottom: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 6,
    elevation: 3,
  },
  cardDesktop: {
    shadowOpacity: 0.12,
    shadowRadius: 10,
    elevation: 5,
  },
  cardRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  cardMain: {
    flex: 1,
    minWidth: 0,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  avatarWrapper: { position: 'relative', marginRight: 4 },
  avatar: {
    width: 52,
    height: 52,
    borderRadius: 26,
    justifyContent: 'center',
    alignItems: 'center',
  },
  avatarText: { fontSize: 18, fontWeight: 'bold' },
  avatarImgWrap: { width: 52, height: 52, borderRadius: 26, overflow: 'hidden' },
  avatarImg: { width: 52, height: 52, borderRadius: 26 },
  unreadBubble: {
    position: 'absolute',
    top: -4,
    right: -6,
    minWidth: 20,
    height: 20,
    borderRadius: 10,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 5,
  },
  unreadText: { fontSize: 11, fontWeight: '700' },

  cardInfo: { flex: 1, gap: 4 },
  cardTopRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 8,
  },
  missionTitle: { fontSize: 15, fontWeight: '700', flex: 1 },
  timestamp: { fontSize: 11, flexShrink: 0 },
  tutorName: { fontSize: 12 },

  progressRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginTop: 2,
  },
  progressTrack: {
    flex: 1,
    height: 4,
    borderRadius: 2,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    borderRadius: 2,
  },
  progressLabel: { fontSize: 11, flexShrink: 0 },

  lastMessage: { fontSize: 13, lineHeight: 18 },

  filterRow: { gap: 8, paddingTop: 12, paddingRight: 4 },
  chip: { borderRadius: 999, borderWidth: 1, paddingHorizontal: 12, paddingVertical: 6, maxWidth: 220 },
  chipText: { fontSize: 12, fontWeight: '600' },

  empty: { alignItems: 'center', gap: 10, paddingVertical: 48 },
  emptyText: { fontSize: 13, textAlign: 'center' },
});


